import AttachmentField from '../components/AttachmentField';
import { submitRequest } from '../lib/submitRequest';
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  GraduationCap,
  Handshake,
  Heart,
  Lightbulb,
  Mic2,
  Network,
  Users,
  X,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { EVENTS } from '../mock';
import MobileCarouselControls from '../components/MobileCarouselControls';

import photoStack1 from '../assets/networking/sortie-bateau.jpg';
import photoStack2 from '../assets/networking/network2.jpg';
import photoStack3 from '../assets/networking/network3.jpg';
import photoStack4 from '../assets/networking/building.jpg';
import photoStack5 from '../assets/networking/event1.jpeg';
import photoStack6 from '../assets/networking/network4.jpg';


const formats = [
  ['Networking entrepreneurial', 'Créer des connexions utiles entre entrepreneurs, dirigeants, experts, investisseurs et partenaires.', Network],
  ['Rencontres entreprises–jeunes talents', 'Faciliter les échanges directs entre les organisations qui recrutent et les talents qui veulent contribuer.', Users],
  ['Événements emploi', 'Donner accès aux opportunités, aux métiers, aux entreprises et aux conseils qui accélèrent l’insertion.', BriefcaseBusiness],
  ['Entrepreneuriat féminin', 'Valoriser les femmes qui entreprennent, créent des emplois et construisent des solutions durables.', Heart],
  ['Conférences', 'Partager des expériences concrètes, des méthodes et des visions capables de faire avancer les participants.', Mic2],
  ['Team Building & ateliers', 'Apprendre, travailler en groupe, structurer une idée et repartir avec des outils directement applicables.', Lightbulb],
  ['Événements partenaires', 'Co-construire des rencontres cohérentes avec les objectifs, les publics et les engagements de nos partenaires.', Handshake],
  ['Transmission & orientation', 'Relier l’apprentissage, les rencontres professionnelles et les perspectives de développement.', GraduationCap],
];

const introSlides = [
  { type: 'mosaic', label: 'Mosaïque' },
  { type: 'panoramic', label: 'Panorama' },
  { type: 'stack', label: 'Perspective' },
];

const teamBuildingTypesOptions = [
  'Activités sportives',
  'Jeux & challenges',
  'Sortie / excursion',
  'Détente & bien-être',
  'Activités en plein air',
];

function EventFormModal({ isOpen, onClose }) {
  const overlayRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  const [form, setForm] = useState({
    entreprise: '',
    departement: '',
    nomResponsable: '',
    email: '',
    telephone: '',
    participants: '',
    dateSouhaitee: '',
    lieuChoisi: 'Non',
    lieuPrecision: '',
    typesTeamBuilding: [],
    autreType: '',
    besoins: '',
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleCheckboxChange = (option) => {
    setForm((prev) => {
      const exists = prev.typesTeamBuilding.includes(option);
      return {
        ...prev,
        typesTeamBuilding: exists
          ? prev.typesTeamBuilding.filter((item) => item !== option)
          : [...prev.typesTeamBuilding, option],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formElement = e.currentTarget;
    setSending(true);
    setError(false);

    try {
      await submitRequest('event', {
          from_name: 'Site MCE Sénégal',
          subject: `Demande de Team Building — ${form.entreprise}`,
          type_de_demande: 'Demande de Team Building',
          entreprise: form.entreprise,
          service_departement: form.departement || 'Non renseigné',
          nom: form.nomResponsable,
          consentement: 'Accepté',
          email: form.email,
          telephone: form.telephone,
          nombre_de_participants: form.participants,
          date_souhaitee: form.dateSouhaitee || 'Non précisée',
          lieu_deja_choisi: form.lieuChoisi,
          precision_lieu: form.lieuChoisi === 'Oui' ? form.lieuPrecision : 'N/A',
          types_de_team_building: form.typesTeamBuilding.length > 0 ? form.typesTeamBuilding.join(', ') : 'Aucun coché',
          autre_type_souhaite: form.autreType || 'Aucun',
          besoins_particuliers: form.besoins || 'Aucun',
      }, formElement);

      setSent(true);
    } catch (err) {
      console.error('Erreur envoi Web3Forms:', err);
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const resetAndClose = () => {
    setSent(false);
    setError(false);
    setForm({
      entreprise: '',
      departement: '',
      nomResponsable: '',
      email: '',
      telephone: '',
      participants: '',
      dateSouhaitee: '',
      lieuChoisi: 'Non',
      lieuPrecision: '',
      typesTeamBuilding: [],
      autreType: '',
      besoins: '',
    });
    onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100"
        style={{ animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Header Modal */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-[#001a3d] to-[#002f6c] text-white">
          <div>
            <h3 className="text-lg font-bold">Formulaire de demande de Team Building</h3>
            <p className="text-xs text-white/70 mt-0.5">Remplissez ces informations pour nous aider à concevoir votre événement.</p>
          </div>
          <button
            type="button"
            onClick={resetAndClose}
            className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h4 className="text-xl font-extrabold text-[#001a3d]">Demande transmise !</h4>
            <p className="text-sm text-slate-600 mt-2 max-w-xs mx-auto">
              Merci pour votre message. Nous étudions vos besoins et revenons vers vous très rapidement.
            </p>
            <button
              type="button"
              onClick={resetAndClose}
              className="mt-6 px-6 py-2.5 rounded-full bg-[#0099CC] text-white font-bold text-sm shadow-md hover:bg-[#0080ab] transition-all"
            >
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[82vh] overflow-y-auto text-left">
            <input type="checkbox" name="botcheck" className="hidden" />

            {error && (
              <div className="flex items-center gap-2 bg-red-50 text-red-600 p-3 rounded-xl text-xs font-semibold border border-red-100">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>Une erreur est survenue lors de l'envoi. Veuillez réessayer ou nous contacter sur WhatsApp.</span>
              </div>
            )}

            {/* Entreprise & Service */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                  Entreprise / Société *
                </label>
                <input
                  required
                  type="text"
                  placeholder="Nom de l'entreprise"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                  value={form.entreprise}
                  onChange={(e) => setForm({ ...form, entreprise: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                  Service / Département
                </label>
                <input
                  type="text"
                  placeholder="Ex: Ressources Humaines"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                  value={form.departement}
                  onChange={(e) => setForm({ ...form, departement: e.target.value })}
                />
              </div>
            </div>

            {/* Responsable */}
            <div>
              <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                Nom et prénom du responsable *
              </label>
              <input
                required
                type="text"
                placeholder="Ex: Fatou Sow"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                value={form.nomResponsable}
                onChange={(e) => setForm({ ...form, nomResponsable: e.target.value })}
              />
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                  Adresse e-mail *
                </label>
                <input
                  required
                  type="email"
                  placeholder="exemple@entreprise.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                  Numéro de téléphone *
                </label>
                <input
                  required
                  type="tel"
                  placeholder="+221 7X XXX XX XX"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                  value={form.telephone}
                  onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                />
              </div>
            </div>

            {/* Participants & Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                  Nombre de participants *
                </label>
                <input
                  required
                  type="number"
                  placeholder="Ex: 25"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                  value={form.participants}
                  onChange={(e) => setForm({ ...form, participants: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                  Date souhaitée
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                  value={form.dateSouhaitee}
                  onChange={(e) => setForm({ ...form, dateSouhaitee: e.target.value })}
                />
              </div>
            </div>

            {/* Lieu déjà choisi ? */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-2">
                Avez-vous déjà choisi un lieu pour l’événement ?
              </label>
              <div className="flex items-center gap-6 mb-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="lieuChoisi"
                    value="Oui"
                    checked={form.lieuChoisi === 'Oui'}
                    onChange={(e) => setForm({ ...form, lieuChoisi: e.target.value })}
                    className="accent-[#0099CC]"
                  />
                  <span>Oui</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="lieuChoisi"
                    value="Non"
                    checked={form.lieuChoisi === 'Non'}
                    onChange={(e) => setForm({ ...form, lieuChoisi: e.target.value })}
                    className="accent-[#0099CC]"
                  />
                  <span>Non</span>
                </label>
              </div>

              {form.lieuChoisi === 'Oui' && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Si oui, précisez le lieu :
                  </label>
                  <input
                    type="text"
                    placeholder="Adresse, hôtel, complexe..."
                    className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                    value={form.lieuPrecision}
                    onChange={(e) => setForm({ ...form, lieuPrecision: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Type de Team Building */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-2">
                Type de Team Building souhaité
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {teamBuildingTypesOptions.map((type) => (
                  <label key={type} className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.typesTeamBuilding.includes(type)}
                      onChange={() => handleCheckboxChange(type)}
                      className="rounded border-slate-300 accent-[#0099CC] w-4 h-4"
                    />
                    <span>{type}</span>
                  </label>
                ))}
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Autre :
                </label>
                <input
                  type="text"
                  placeholder="Précisez un autre type si besoin"
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all text-slate-800"
                  value={form.autreType}
                  onChange={(e) => setForm({ ...form, autreType: e.target.value })}
                />
              </div>
            </div>

            {/* Besoins ou demandes particulières */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-xs font-semibold text-[#001a3d] uppercase tracking-wider mb-1">
                Besoins ou demandes particulières
              </label>
              <textarea
                rows={3}
                placeholder="Saisissez ici vos attentes spécifiques, contraintes ou objectifs..."
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-[#0099CC] focus:bg-white transition-all resize-none text-slate-800"
                value={form.besoins}
                onChange={(e) => setForm({ ...form, besoins: e.target.value })}
              />
            </div>

            <AttachmentField />
            <label className="flex gap-2 text-sm"><input type="checkbox" required />J’accepte le traitement de ces informations pour répondre à ma demande.</label>
            {/* Boutons de soumission et de contact Calendly */}
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button
                type="submit"
                disabled={sending}
                className="flex-1 py-3.5 px-6 rounded-xl font-bold text-white bg-gradient-to-r from-[#0099CC] to-[#006fa1] shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer"
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>ENVOI EN COURS…</span>
                  </>
                ) : (
                  <span>Envoyer la demande</span>
                )}
              </button>

              <a
                href="https://calendly.com/amane-mounia/coaching-decouverte-gratuit"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3.5 px-6 rounded-xl font-bold text-[#001a3d] bg-slate-100 hover:bg-slate-200 active:scale-[0.99] transition-all text-center flex items-center justify-center gap-2 border border-slate-200"
              >
                Contactez-nous
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function NetworkingEvenements() {
  const [activeEvent, setActiveEvent] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const totalSlides = introSlides.length;

  useEffect(() => {
    const timer = setInterval(() => setActiveEvent((index) => (index + 1) % EVENTS.length), 5200);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveSlide((i) => (i + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(timer);
  }, [isPaused, totalSlides]);

  return (
    <main className="pt-[72px] networking-page">

      {/* HERO DESKTOP */}
      <section
        className="networking-hero hidden md:block"
        style={{
          backgroundImage: `url('/assets/networking-conference-desktop.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="max-w-[1200px] mx-auto px-6">
          <h1 style={{ color: '#111111' }}>
            Team Building & networking
            <br />
            <em style={{ color: '#141313' }}>Créer des opportunités.</em>
          </h1>
          <p className="networking-hero-description networking-hero-description-desktop" style={{ color: '#1d1b1b' }}>
            MCE conçoit des événements professionnels et à impact pour connecter les entreprises, les entrepreneurs, les jeunes talents, les femmes leaders et les partenaires autour d’expériences utiles.
          </p>

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="networking-talk-button mt-7 inline-flex items-center gap-3 px-8 py-3.5 rounded-xl text-base cursor-pointer transition-transform active:scale-95 shadow-md"
            style={{
              backgroundColor: '#61a0d3',
              border: '2px solid #081728',
              color: '#000000',
              fontWeight: 900
            }}
          >
            <span style={{ color: '#000000', fontWeight: 900 }}>Parlons-en</span>
            <ArrowRight size={18} style={{ color: '#000000', stroke: '#000000', strokeWidth: 3 }} />
          </button>
        </div>
      </section>

      {/* HERO MOBILE */}
      <section
        className="md:hidden relative overflow-hidden"
        style={{
          backgroundImage: `url('/assets/networking-conference-mobile.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          minHeight: '640px',
        }}
      >
        <div className="px-6 pt-8 pb-6 text-center relative z-10">
          <h1 className="font-black leading-[1.15] tracking-tight text-[clamp(1.6rem,6vw,2.1rem)]" style={{ color: '#111111' }}>
            Team Building & networking
            <br />
            <em className="not-italic" style={{ color: '#141414' }}>Créer des opportunités.</em>
          </h1>

          <div className="mt-4 h-[3px] w-12 mx-auto rounded-full" style={{ backgroundColor: '#1a1b1b' }} />

          <p className="mt-4 text-[13.5px] leading-[1.55] max-w-[340px] mx-auto" style={{ color: '#1d1b1b' }}>
            MCE crée des événements à impact qui connectent entreprises, talents et partenaires.
          </p>

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="networking-talk-button mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm cursor-pointer shadow-lg transition-transform active:scale-95"
            style={{ backgroundColor: '#61a0d3', color: '#081728', border: '2px solid #081728' }}
          >
            Parlons-en
            <ArrowRight className="w-4 h-4 stroke-[2.5]" />
          </button>
        </div>
      </section>

      {/* SECTION CARROUSEL & INTENTION */}
      <section
        className="relative overflow-hidden pt-8 pb-16 lg:pt-12 lg:pb-24"
        style={{ background: 'linear-gradient(180deg, #f9fafc 0%, #ffffff 60%, #f4f7fb 100%)' }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10 pt-5">
          <div className="relative min-h-[700px] sm:min-h-[620px] md:min-h-[560px] lg:min-h-[464px]">
            <div className="absolute inset-0 grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div className="relative rounded-2xl overflow-hidden p-4 bg-white shadow-xl">
                <div className="grid grid-cols-2 gap-3">
                  <img src={photoStack1} alt="Sortie en bateau en groupe" className="w-full h-40 object-cover rounded-lg" />
                  <img src={photoStack2} alt="Soirée networking" className="w-full h-40 object-cover rounded-lg" />
                  <img src={photoStack3} alt="Femmes leaders" className="w-full h-40 object-cover rounded-lg" />
                  <img src={photoStack4} alt="Afterwork GOFAR" className="w-full h-40 object-cover rounded-lg" />
                </div>
              </div>

              <div>
                <h2 className="text-2xl md:text-3xl lg:text-[36px] font-black leading-tight text-[#001a3d]">
                  Des rencontres qui ne s’arrêtent pas à l’échange de cartes de visite.
                </h2>
                <div className="h-1 w-14 rounded-full mt-4 mb-6 bg-[#d4a017]" />
                <p className="text-[14px] lg:text-[15px] leading-relaxed max-w-xl text-[#001a3d]/75">
                  Chaque format est pensé pour provoquer des connexions pertinentes, faire circuler l’information, révéler des talents et transformer une rencontre en collaboration, recrutement, apprentissage ou projet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="networking-formats">
        <div className="max-w-[1200px] mx-auto px-6">
          <header>
            <h2>Des événements adaptés aux communautés et aux objectifs</h2>
          </header>
          <div id="networking-formats-carousel">
            {formats.map(([title, description, Icon]) => (
              <article key={title}>
                <Icon />
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
          <MobileCarouselControls targetId="networking-formats-carousel" />
        </div>
      </section>

      {/* SECTION PARTENAIRES & BOUTON FORMULAIRE */}
      <section className="networking-partners">
        <div className="max-w-[1100px] mx-auto px-6">
          <CalendarDays />
          <div>
            <h2>Un événement à imaginer avec MCE ?</h2>
            <p>Définissons le public, le format, les partenaires, le programme et l’expérience attendue.</p>
          </div>

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-3 px-7 py-3 rounded-full font-bold text-sm cursor-pointer transition-all hover:bg-white/10 active:scale-95 border border-[#5b9bd5] text-white bg-transparent"
          >
            <span>Parler de votre événement</span>
            <ArrowRight size={16} className="text-[#d4a017]" />
          </button>
        </div>
      </section>

      <div className="networking-footer-space" aria-hidden="true" />

      {/* Modal Formulaire de demande de Team Building */}
      <EventFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </main>
  );
}
